import { Box } from '@tempmee/react';
import { CenteredSpinner, Pagination } from '~/commonComponents';
import { useCallback } from 'react';
import { DashboardJobsStatus, DashboardJobsType, IDashboardJob } from '~/domain/DashboardJobs';
import {
  fireActionNeededShiftViewShiftClicked,
  fireOfficeNotificationOfferExpirationWarning,
} from '~/posthogs/events';
import { ConfirmCandidateSource } from '~/domain/ConfirmCandidateSource';
import { buildEngagementContext } from '~/domain/OfficeNotification';
import { useNavigateToConfirmCandidate } from '~/hooks/useNavigateToConfirmCandidate';
import { useOfficeNotificationLanding } from '~/hooks/useOfficeNotificationLanding';
import { FilterStatusEnum } from '~/utils/Enum';
import { useJobList } from '../../hooks';
import { EmptyMessage } from '../EmptyMessage';
import { ShiftOverviewCard } from '../ShiftOverviewCard';
import { ShiftOverviewList } from '../ShiftOverviewList';
import { ExpirationUpdatePopup } from './ExpirationUpdatePopup';

interface IPendingTabProps {
  jobType: DashboardJobsType;
}

export const PendingTab = ({ jobType }: IPendingTabProps) => {
  const navigateToConfirmCandidate = useNavigateToConfirmCandidate();

  const { data, isInitialLoading, methods, pagination } = useJobList({
    status: DashboardJobsStatus.ACTION_REQUIRED,
    jobType,
  });

  // Email-activity tracking (GOTU-4002 / GOTU-4135): an office landing on the
  // Action Needed list from an "offer expiration warning" email CTA reports the
  // expiring batch the email was built for, carried on the link's `shift_ids`.
  useOfficeNotificationLanding({
    enabled: !isInitialLoading,
    fallbackKey: 'offer_expiration_warning',
    handlers: {
      offer_expiration_warning: ({ origin, clickedAt, shiftIds }) =>
        fireOfficeNotificationOfferExpirationWarning({
          origin,
          shiftIds,
          engagement: buildEngagementContext({ clickedAt }),
        }),
    },
  });

  const handleCardClick = useCallback(
    (item: IDashboardJob) => {
      const counters = item?.multipleCounters ?? [];
      const favoriteCandidate = counters.find((counter) => !!counter.isFavorite);

      const candidateId = (favoriteCandidate ?? counters?.[0])?.candidate?.id;
      const shiftId = item?.id;

      if (candidateId && shiftId) {
        fireActionNeededShiftViewShiftClicked({ shiftId, candidateCount: counters.length });
        // Attribute a later confirm on this shift to the Action Needed list
        // (GOTU-4906).
        navigateToConfirmCandidate({
          shiftId,
          candidateId,
          candidateCount: counters.length,
          isOnsiteInterview: Boolean(item.dashboardJobApi?.isOnsiteInterview),
          source: ConfirmCandidateSource.ActionNeeded,
        });
      }
    },
    [navigateToConfirmCandidate],
  );

  if (isInitialLoading) {
    return (
      <Box pt={8}>
        <CenteredSpinner />
      </Box>
    );
  }

  return (
    <>
      <ShiftOverviewList
        dataCy="action-needed-table"
        isEmpty={data.length === 0}
        EmptyComponent={<EmptyMessage type={FilterStatusEnum.PENDING} />}
        PaginationComponent={
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={methods.setCurrentPage}
            maxButtons={5}
          />
        }
      >
        {data.map((item) => (
          <ShiftOverviewCard
            key={item.id}
            item={item}
            status="confirm-candidates"
            onCardClick={handleCardClick}
            invite={{ shiftStatus: 'Action Needed', trackingSource: 'Action Needed' }}
            invitePlacement="menu"
            canCancel
          />
        ))}
      </ShiftOverviewList>
      <ExpirationUpdatePopup />
    </>
  );
};
